'use client'

import { RecipeVariantProps, css, cva, cx } from '@/styled-system/css'
import { Flex, splitCssProps, type HTMLStyledProps } from '@/styled-system/jsx'
import { memo } from 'react'
import ChipStack from '../shared/chipStack'

const styles = cva({
  base: {
    borderRadius: '0.25em',
    cursor: 'pointer',
    outline: 'none',
    position: 'relative',
    transitionDuration: '0.2s',
    transitionProperty: 'background-color, box-shadow, opacity',
  },
  variants: {
    variant: {
      black: {
        bgColor: 'neutral.300',
        _hover: {
          bgColor: 'neutral.400',
          boxShadow: `inset 0 0 0 0.125em #fff`,
        },
        _highlighted: {
          bgColor: 'neutral.400',
          boxShadow: `inset 0 0 0 0.125em #fff`,
        },
      },
      red: {
        bgColor: 'red.500',
        _hover: {
          bgColor: 'red.400',
          boxShadow: `inset 0 0 0 0.125em #fff`,
        },
        _highlighted: {
          bgColor: 'red.400',
          boxShadow: `inset 0 0 0 0.125em #fff`,
        },
      },
      green: {
        bgColor: '#086D59',
        _hover: {
          bgColor: '#16856F',
          boxShadow: `inset 0 0 0 0.125em #fff`,
        },
        _highlighted: {
          bgColor: '#16856F',
          boxShadow: `inset 0 0 0 0.125em #fff`,
        },
      },
      outlined: {
        h: { base: '40px', md: 'normal' },
        boxShadow: `inset 0 0 0 .0625em token(colors.neutral.300)`,
        _hover: {
          bgColor: 'neutral.300',
          boxShadow: `inset 0 0 .0.0625em token(colors.neutral.400)`,
        },
      },
    },
    orientation: {
      horizontal: {},
      vertical: {},
    },
    active: {
      true: {
        outlineWidth: '3px',
        outlineStyle: 'solid',
        outlineColor: 'success',
      },
    },
  },
  compoundVariants: [
    {
      variant: 'outlined',
      orientation: 'vertical',
      css: {
        writingMode: 'vertical-lr',
      },
    },
  ],
})

export type RouletteGamePocketProps = HTMLStyledProps<'button'> &
  RecipeVariantProps<typeof styles> & {
    area: string
    totalChips?: number
    highlighted?: boolean
  }

const RouletteGamePocket = memo(function RouletteGamePocket(
  props: RouletteGamePocketProps
) {
  const [variantProps, pocketProps] = styles.splitVariantProps(props)
  const [cssProps, localProps] = splitCssProps(pocketProps)
  const {
    children,
    area,
    totalChips = 0,
    highlighted,
    style,
    ...buttonProps
  } = localProps

  return (
    <button
      className={cx(styles(variantProps), css(cssProps))}
      data-highlighted={highlighted ? 'true' : undefined}
      style={{
        gridArea: area,
        ...style,
      }}
      {...buttonProps}
    >
      {children ? (
        <Flex
          align='center'
          justify='center'
          position='absolute'
          top={0}
          left={0}
          right={0}
          bottom={0}
          fontSize='1em'
          lineHeight='normal'
          fontWeight='bold'
          pointerEvents='none'
        >
          {children}
        </Flex>
      ) : null}

      <Flex
        align='center'
        justify='center'
        position='absolute'
        top={0}
        left={0}
        right={0}
        bottom={0}
        writingMode='initial'
        _hover={{
          transform: 'scale(1.2)',
        }}
      >
        <ChipStack amount={totalChips} />
      </Flex>
    </button>
  )
})

export default RouletteGamePocket
